/**
 * useSpeechRecognition — thin wrapper around the browser's Web Speech API
 * (webkitSpeechRecognition / SpeechRecognition). Free, no backend.
 *
 * Usage:
 *   const { supported, listening, transcript, start, stop, error, reset }
 *     = useSpeechRecognition({ lang: 'es-AR', interim: true });
 *
 * Returns an incremental transcript while `listening`. Callers usually
 * append/replace their own textarea state on each transcript change.
 *
 * Chrome/Edge/Safari support this API without a paid backend. Firefox
 * does not; the hook reports `supported=false` in that case.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const getRecognitionCtor = () => {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

export const useSpeechRecognition = ({ lang = 'es-AR', interim = true } = {}) => {
  const RecognitionCtor = getRecognitionCtor();
  const supported = !!RecognitionCtor;

  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState(null);
  const recognitionRef = useRef(null);
  const finalRef = useRef('');   // accumulated final result across the session
  const shouldRestartRef = useRef(false);

  // Lazily instantiate a SpeechRecognition object (persisted across renders).
  useEffect(() => {
    if (!supported) return undefined;
    const rec = new RecognitionCtor();
    rec.lang = lang;
    rec.continuous = true;
    rec.interimResults = interim;
    rec.maxAlternatives = 1;

    rec.onresult = (event) => {
      let interimText = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const res = event.results[i];
        if (res.isFinal) {
          finalRef.current += (finalRef.current ? ' ' : '') + res[0].transcript.trim();
        } else {
          interimText += res[0].transcript;
        }
      }
      const combined = interimText
        ? `${finalRef.current}${finalRef.current ? ' ' : ''}${interimText}`.trim()
        : finalRef.current;
      setTranscript(combined);
    };

    rec.onerror = (e) => {
      // 'no-speech' fires on silence — ignore, keep listening.
      if (e.error === 'no-speech' || e.error === 'aborted') return;
      setError(e.error || 'speech-error');
      shouldRestartRef.current = false;
      setListening(false);
    };

    rec.onend = () => {
      // Chrome auto-stops after ~1min of silence; auto-restart if the user
      // hasn't explicitly toggled off.
      if (shouldRestartRef.current) {
        try { rec.start(); } catch (_) { /* re-entrancy — ignore */ }
      } else {
        setListening(false);
      }
    };

    recognitionRef.current = rec;
    return () => {
      shouldRestartRef.current = false;
      try { rec.stop(); } catch (_) { /* noop */ }
      recognitionRef.current = null;
    };
    // We intentionally ignore lang/interim changes after mount — remount if
    // the caller passes new values (rare).
  }, [supported]);

  const start = useCallback(() => {
    if (!supported || !recognitionRef.current) return;
    setError(null);
    setTranscript('');
    finalRef.current = '';
    shouldRestartRef.current = true;
    try {
      recognitionRef.current.start();
      setListening(true);
    } catch (e) {
      // Firefox / already-started safeguard
      setError(e?.message || 'start-failed');
      setListening(false);
    }
  }, [supported]);

  const stop = useCallback(() => {
    shouldRestartRef.current = false;
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (_) { /* noop */ }
    }
    setListening(false);
  }, []);

  const reset = useCallback(() => {
    finalRef.current = '';
    setTranscript('');
    setError(null);
  }, []);

  return { supported, listening, transcript, error, start, stop, reset };
};

export default useSpeechRecognition;
